import React, { useEffect, useState } from 'react';
import { Box, TextField, Typography } from '@mui/material';

interface DocumentNumberFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  required?: boolean;
  fechaExtraccion?: string;
}

const DocumentNumberField: React.FC<DocumentNumberFieldProps> = ({
  label,
  value,
  onChange,
  error,
  required = false,
  fechaExtraccion,
}) => {
  const [documentNumber, setDocumentNumber] = useState('');
  const [year, setYear] = useState('');
  const [month, setMonth] = useState('');

  // Parse the current value to extract the document number
  useEffect(() => {
    if (value) {
      const match = value.match(/^(\d+)-(\d{4})-(\d{2})\/SEC$/);
      if (match) {
        setDocumentNumber(match[1]);
        setYear(match[2]);
        setMonth(match[3]);
      } else {
        // If value doesn't match format, try to extract just the number at the beginning
        const numberMatch = value.match(/^(\d+)/);
        setDocumentNumber(numberMatch ? numberMatch[1] : '');
      }
    } else {
      setDocumentNumber('');
    }
  }, [value]);

  // Update year and month when fechaExtraccion changes
  useEffect(() => {
    if (fechaExtraccion) {
      const date = new Date(fechaExtraccion);
      const newYear = date.getFullYear().toString();
      const newMonth = (date.getMonth() + 1).toString().padStart(2, '0');
      setYear(newYear);
      setMonth(newMonth);

      // Update the full document number if we have a document number
      if (documentNumber) {
        const fullDocumentNumber = `${documentNumber}-${newYear}-${newMonth}/SEC`;
        onChange(fullDocumentNumber);
      }
    }
  }, [fechaExtraccion, documentNumber, onChange]);

  const handleDocumentNumberChange = (newDocumentNumber: string) => {
    // Only allow numbers
    const numericValue = newDocumentNumber.replace(/\D/g, '');
    setDocumentNumber(numericValue);

    // Create the full document number format
    if (numericValue && year && month) {
      const fullDocumentNumber = `${numericValue}-${year}-${month}/SEC`;
      onChange(fullDocumentNumber);
    } else {
      onChange(numericValue);
    }
  };

  const displayValue = documentNumber && year && month
    ? `${documentNumber}-${year}-${month}/SEC`
    : documentNumber;

  return (
    <Box>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 1,
          color: 'text.secondary',
          fontSize: '0.875rem'
        }}
      >
        {label} {required && <span style={{ color: 'error.main' }}>*</span>}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          value={documentNumber}
          onChange={(e) => handleDocumentNumberChange(e.target.value)}
          placeholder="0000"
          size="small"
          sx={{ width: '80px' }}
          inputProps={{
            maxLength: 4,
            style: { textAlign: 'center' }
          }}
          error={!!error}
        />

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>

        <TextField
          value={year}
          size="small"
          sx={{ width: '80px' }}
          InputProps={{
            readOnly: true,
            style: { textAlign: 'center', backgroundColor: '#f5f5f5' }
          }}
          placeholder="YYYY"
        />

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          -
        </Typography>

        <TextField
          value={month}
          size="small"
          sx={{ width: '50px' }}
          InputProps={{
            readOnly: true,
            style: { textAlign: 'center', backgroundColor: '#f5f5f5' }
          }}
          placeholder="MM"
        />

        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          /SEC
        </Typography>
      </Box>

      {displayValue && (
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: 'block',
            color: 'text.secondary',
            fontStyle: 'italic'
          }}
        >
          Resultado: {displayValue}
        </Typography>
      )}

      {error && (
        <Typography
          variant="caption"
          sx={{
            mt: 1,
            display: 'block',
            color: 'error.main'
          }}
        >
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default DocumentNumberField;